import { useMemo, useState } from 'react';
import { Alert, Avatar, Button, Input, Spin, Typography } from 'antd';
import { IconChevronRight, IconChevronDown, IconTableOptions, IconUpload, IconDownload } from '@tabler/icons-react';

import { useTrainingMatrix } from '@/entities/training/api/libraryApi';
import type { MatrixPosition, MatrixProgram } from '@/entities/training/model/librarySchema';

import { ProgramDetailDrawer } from '../LibraryTab/ProgramDetailDrawer';
import s from './MatrixTab.module.scss';

const { Text, Title } = Typography;

const RECURRENCE_LABELS: Record<string, string> = {
  cyclic: 'Периодическое',
  one_time: 'Разовое',
};

function recurrenceLabel(model: string | null | undefined): string {
  if (!model) return '—';
  return RECURRENCE_LABELS[model] ?? model;
}

function ProfessionRow({
  pos,
  onProgramClick,
}: {
  pos: MatrixPosition;
  onProgramClick: (prog: MatrixProgram) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={s.root__profRow}>
      <div className={s.root__profHeader} onClick={() => setExpanded(!expanded)}>
        {expanded ? (
          <IconChevronDown size={16} className={s.root__chevronActive} />
        ) : (
          <IconChevronRight size={16} className={s.root__chevron} />
        )}
        <div className={s.root__profInfo}>
          <div className={s.root__profName}>{pos.position_name}</div>
          <div className={s.root__profMeta}>{pos.employee_count} сотрудников</div>
        </div>
        <span className={`${s.root__profBadge} ${expanded ? s.root__profBadge_active : ''}`}>
          {pos.programs.length} программ
        </span>
      </div>

      {expanded ? (
        <div className={s.root__programs}>
          <div className={s.root__programHeader}>
            <div />
            <div>Программа</div>
            <div>Периодичность</div>
            <div>Тип</div>
            <div>Код</div>
          </div>
          {pos.programs.map((p) => (
            <div
              key={p.program_id}
              className={`${s.root__programRow} ${s.root__programRow_clickable}`}
              onClick={() => onProgramClick(p)}
            >
              <div />
              <div className={s.root__programName}>{p.program_name}</div>
              <div className={s.root__programDetail}>{recurrenceLabel(p.recurrence_model)}</div>
              <div className={s.root__programDetail}>{p.is_external ? 'Внешнее' : 'Внутреннее'}</div>
              <div className={s.root__programSource}>{p.program_code ?? '—'}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function MatrixTab() {
  const { data, isLoading, error } = useTrainingMatrix();
  const [search, setSearch] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);

  const positions = data?.positions ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return positions;
    const q = search.toLowerCase();
    return positions.filter(
      (p) =>
        p.position_name.toLowerCase().includes(q) ||
        p.programs.some((prog) => prog.program_name.toLowerCase().includes(q)),
    );
  }, [search, positions]);

  const handleProgramClick = (prog: MatrixProgram) => {
    setSelectedProgramId(prog.program_id);
  };

  return (
    <div className={s.root}>
      <div className={s.root__hero}>
        <div className={s.root__heroRow}>
          <Avatar size={56} className={s.root__heroAvatar} icon={<IconTableOptions size={26} />} />
          <div className={s.root__heroText}>
            <div className={s.root__heroLabel}>Справочник</div>
            <Title level={4} className={s.root__heroName}>
              Матрица обучения
            </Title>
            <Text className={s.root__heroMeta}>Версия от 15.04.2026 · Применил: Козлова И.В.</Text>
            <div className={s.root__heroBadges}>
              <span className={s.root__heroBadge}>{data?.totalPositions ?? '…'} профессий</span>
              <span className={s.root__heroBadge}>{data?.totalPrograms ?? '…'} программ</span>
              <span className={s.root__heroBadge}>Действует</span>
            </div>
          </div>
          <div className={s.root__heroActions}>
            <Button className={s.root__heroBtn} disabled icon={<IconUpload size={14} />}>
              Загрузить
            </Button>
            <Button className={s.root__heroBtn} disabled icon={<IconDownload size={14} />}>
              Выгрузить
            </Button>
          </div>
        </div>
      </div>

      <Alert
        type="warning"
        showIcon
        className={s.root__demoAlert}
        message="Раздел в режиме просмотра для демо. Загрузка и редактирование будут доступны в следующей версии."
      />

      <div className={s.root__toolbar}>
        <Input.Search
          placeholder="Поиск по профессии или программе…"
          className={s.root__search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <span className={s.root__counter}>
          {filtered.length} из {positions.length}
        </span>
      </div>

      {isLoading ? (
        <div className={s.root__loading}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message="Не удалось загрузить матрицу обучения" className={s.root__errorAlert} />
      ) : (
        <div className={s.root__list}>
          {filtered.map((pos) => (
            <ProfessionRow key={pos.position_id} pos={pos} onProgramClick={handleProgramClick} />
          ))}
        </div>
      )}

      {filtered.length === 0 && !isLoading ? (
        <div className={s.root__empty}>
          Нет профессий с такими параметрами
          <Button type="link" size="small" onClick={() => setSearch('')}>
            Сбросить фильтры
          </Button>
        </div>
      ) : null}

      <ProgramDetailDrawer programId={selectedProgramId} onClose={() => setSelectedProgramId(null)} />
    </div>
  );
}
